import { useEffect, useId, useRef, useState, type CSSProperties, type PointerEvent as ReactPointerEvent, type ReactNode } from 'react';
import { defineMessages, useIntl } from 'react-intl';

const m = defineMessages({
  close: { id: 'sheet.close', defaultMessage: 'Close', description: 'Accessible label of the close button of a sheet / dialog' },
});

const FOCUSABLE = 'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';

/** The drag past which a swipe-down closes the sheet (px); a shorter one springs back. */
export const SWIPE_CLOSE_THRESHOLD = 72;
/** A quick flick closes from a shorter drag (px per ms). */
const SWIPE_CLOSE_VELOCITY = 0.6;
/** Where a drag may start: the handle and the head, never a control (a tap on Close is a tap). */
const SWIPE_FROM = '.sheet-handle, .sheet-head, .assist-head';
const SWIPE_NOT_FROM = 'button, a, input, select, textarea, [role="separator"]';

export interface SwipeToClose {
  onPointerDown: (e: ReactPointerEvent<HTMLElement>) => void;
  onPointerMove: (e: ReactPointerEvent<HTMLElement>) => void;
  onPointerUp: (e: ReactPointerEvent<HTMLElement>) => void;
  onPointerCancel: (e: ReactPointerEvent<HTMLElement>) => void;
  /** The sheet follows the finger (`transform`), or stays put under reduced motion; `undefined` at rest. */
  style: CSSProperties | undefined;
  /** True while a drag is on, for `data-dragging`. */
  dragging: boolean;
}

/**
 * Swipe-down to close a phone sheet (planning-app.md §4.1, MOB12; the critique's §3.17): pointer
 * events on the sheet's element, a drag that starts on the handle or the head follows the finger,
 * and past `SWIPE_CLOSE_THRESHOLD` (or a flick) the release closes; a shorter one springs back.
 * Under reduced motion the sheet does not move while dragged and there is no spring — the release
 * still closes. `enabled` false (a desktop dialog, an action in flight) makes every handler a no-op.
 */
export function useSwipeToClose(onClose: () => void, enabled: boolean): SwipeToClose {
  const drag = useRef<{ id: number; startY: number; startT: number; dy: number } | null>(null);
  const [dy, setDy] = useState(0);
  const [dragging, setDragging] = useState(false);
  const reduced = typeof window !== 'undefined' && typeof window.matchMedia === 'function' && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  const end = (e: ReactPointerEvent<HTMLElement>, close: boolean) => {
    const d = drag.current;
    if (!d || d.id !== e.pointerId) return;
    drag.current = null;
    if (e.currentTarget.hasPointerCapture(e.pointerId)) e.currentTarget.releasePointerCapture(e.pointerId);
    setDragging(false);
    setDy(0);
    if (close) onClose();
  };
  return {
    onPointerDown: (e) => {
      if (!enabled || drag.current || (e.pointerType === 'mouse' && e.button !== 0)) return;
      const target = e.target as HTMLElement;
      if (!target.closest(SWIPE_FROM) || target.closest(SWIPE_NOT_FROM)) return;
      drag.current = { id: e.pointerId, startY: e.clientY, startT: e.timeStamp, dy: 0 };
      e.currentTarget.setPointerCapture(e.pointerId);
      setDragging(true);
    },
    onPointerMove: (e) => {
      const d = drag.current;
      if (!d || d.id !== e.pointerId) return;
      d.dy = Math.max(0, e.clientY - d.startY);
      if (!reduced) setDy(d.dy);
    },
    onPointerUp: (e) => {
      const d = drag.current;
      if (!d || d.id !== e.pointerId) return;
      const dt = Math.max(1, e.timeStamp - d.startT);
      end(e, d.dy >= SWIPE_CLOSE_THRESHOLD || (d.dy >= 24 && d.dy / dt >= SWIPE_CLOSE_VELOCITY));
    },
    onPointerCancel: (e) => end(e, false),
    style: dy > 0 ? { transform: `translateY(${dy}px)` } : undefined,
    dragging,
  };
}

export interface SheetProps {
  /** The heading; also the dialog's accessible name. */
  title: ReactNode;
  onClose: () => void;
  children: ReactNode;
  /** Buttons; laid out end-aligned on desktop, full width on phones. */
  actions?: ReactNode;
  /** Wider variant for reading (citation text, pre-fills). */
  wide?: boolean;
  /** Optional helper line under the title. */
  description?: ReactNode;
  /** Hooks for tests. */
  testId?: string;
  /** When false the backdrop and Escape do not close (an action in flight). */
  dismissable?: boolean;
  /** On a phone a drag down on the handle or the head closes the sheet (`useSwipeToClose`); off by default. */
  swipeToClose?: boolean;
  /** A 52px full-width Close row at the bottom of a phone sheet, inside the safe area, for the hand that opened it from the tab bar; off by default. A node replaces the label. */
  closeRow?: boolean | ReactNode;
}

/**
 * The design-system bottom sheet (planning-app.md §4.1, MOB12; `design-system/bottom-sheet.html`):
 * a modal dialog that rises from the bottom edge on phones, inside the safe area, with a drag
 * handle for the eye and a Close button for the hand; a centred dialog on desktop. Focus moves
 * in on open, cycles inside, and returns to the opener on close; Escape and the backdrop close
 * it; the page behind stops scrolling. Reduced motion turns the slide into a plain appearance.
 */
export function Sheet({ title, onClose, children, actions, wide, description, testId, dismissable = true, swipeToClose = false, closeRow = false }: SheetProps) {
  const intl = useIntl();
  const id = useId();
  const ref = useRef<HTMLDivElement>(null);
  const openerRef = useRef<Element | null>(null);
  const swipe = useSwipeToClose(onClose, swipeToClose && dismissable);

  useEffect(() => {
    openerRef.current = document.activeElement;
    const node = ref.current;
    const first = node?.querySelector<HTMLElement>('[data-autofocus]') ?? node?.querySelector<HTMLElement>(FOCUSABLE);
    (first ?? node)?.focus();
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = previousOverflow;
      const opener = openerRef.current;
      if (opener instanceof HTMLElement && opener.isConnected) opener.focus();
    };
  }, []);

  const onKeyDown = (e: React.KeyboardEvent<HTMLDivElement>) => {
    if (e.key === 'Escape') {
      if (dismissable) {
        e.stopPropagation();
        onClose();
      }
      return;
    }
    if (e.key !== 'Tab' || !ref.current) return;
    const items = [...ref.current.querySelectorAll<HTMLElement>(FOCUSABLE)].filter((el) => el.offsetParent !== null || el === document.activeElement);
    if (items.length === 0) {
      e.preventDefault();
      return;
    }
    const firstItem = items[0]!;
    const lastItem = items[items.length - 1]!;
    if (e.shiftKey && (document.activeElement === firstItem || document.activeElement === ref.current)) {
      e.preventDefault();
      lastItem.focus();
    } else if (!e.shiftKey && document.activeElement === lastItem) {
      e.preventDefault();
      firstItem.focus();
    }
  };

  return (
    <div className="sheet-backdrop" role="presentation" onClick={dismissable ? onClose : undefined} data-testid={testId ? `${testId}-backdrop` : undefined}>
      <div
        ref={ref}
        className={wide ? 'sheet wide' : 'sheet'}
        role="dialog"
        aria-modal="true"
        aria-labelledby={`${id}-h`}
        aria-describedby={description ? `${id}-d` : undefined}
        tabIndex={-1}
        onClick={(e) => e.stopPropagation()}
        onKeyDown={onKeyDown}
        onPointerDown={swipe.onPointerDown}
        onPointerMove={swipe.onPointerMove}
        onPointerUp={swipe.onPointerUp}
        onPointerCancel={swipe.onPointerCancel}
        style={swipe.style}
        data-dragging={swipe.dragging || undefined}
        data-swipe={swipeToClose || undefined}
        data-testid={testId}
      >
        <span className="sheet-handle" aria-hidden="true" />
        <div className="sheet-head">
          <h2 id={`${id}-h`}>{title}</h2>
          <button type="button" className="sheet-x" onClick={onClose} aria-label={intl.formatMessage(m.close)}>
            <svg className="ic" viewBox="0 0 24 24" aria-hidden="true">
              <path d="M6 6l12 12M18 6L6 18" />
            </svg>
          </button>
        </div>
        {description && (
          <p id={`${id}-d`} className="muted">
            {description}
          </p>
        )}
        <div className="sheet-body">{children}</div>
        {actions && <div className="row end sheet-actions">{actions}</div>}
        {closeRow && (
          <div className="sheet-close-row">
            <button type="button" className="btn sheet-close-btn" onClick={onClose} data-testid={testId ? `${testId}-close-row` : undefined}>
              {closeRow === true ? intl.formatMessage(m.close) : closeRow}
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
